import {
  breakpointSizes,
  Checkbox,
  getBreakpointDown,
  getFontCss,
  Processing,
  styleConstants,
  Tag,
  TagColorSchemes,
  themeConstants,
} from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectTransactionIdFromPathState } from 'src/app/TransactionDetail/redux/transactionDetail.selectors'
import AttachmentsList from 'src/components/AttachmentsList/AttachmentsList'
import Box from 'src/components/Box/Box'
import ErrorList from 'src/components/ErrorList/ErrorList'
import SvgArrowForward from 'src/components/Svgs/SvgArrowForward'
import SvgReceiptMissing from 'src/components/Svgs/SvgReceiptMissing'
import SvgReceiptUpload from 'src/components/Svgs/SvgReceiptUpload'
import TestId from 'src/components/TestId/TestId'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import { ExtendCurrency, OrganizationConnectedPlatform } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import Crncy from 'src/localization/Crncy'
import Trns from 'src/localization/Trns'
import { OrganizationReceiptRulesResponse } from 'src/models/organizations.models'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { useQueryActiveTransactionIntegration } from 'src/queries/integrations/orgIntegrations.queries'
import { updateTransactionDetailResponse } from 'src/queries/transactions.queries'
import {
  POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS,
  postERPReceiptAttachments,
} from 'src/redux/actions/data/receiptAttachments.actions'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectIsPushingAttachmentsToERPFromState } from 'src/redux/selectors/data/receiptAttachments.selectors'
import { PatchTransactionRequest } from 'src/requests/patchTransaction.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { toDollars } from 'src/utils/currency.utils'
import { css } from 'styled-components'

interface ReceiptsBoxProps {
  className?: string
  attachments: ReceiptAttachmentModel[]
  deleteAttachment?: (attachmentId: string) => void
  topLevelError?: string
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  uploadingAttachmentThumbnails?: Record<string, string>
  isReceiptRequired: boolean
  isTransaction?: boolean
  errors?: boolean
  isReceiptSectionVisible?: boolean
  patchTransaction?: (data: Partial<PatchTransactionRequest>) => void
  receiptRule?: OrganizationReceiptRulesResponse
}

const ReceiptsBox: React.FC<ReceiptsBoxProps> = ({
  attachments,
  deleteAttachment,
  topLevelError,
  uploadAttachments,
  uploadingAttachmentThumbnails,
  isReceiptRequired,
  isTransaction,
  errors,
  isReceiptSectionVisible,
  patchTransaction,
  receiptRule,
}): React.ReactElement => {
  const queryClient = useQueryClient()

  const { analytics } = useAnalyticsContext()
  const [hasErrors, setHasErrors] = useState(false)
  const [receiptRequired, setReceiptRequired] = useState(isReceiptRequired)
  const [attachmentError, setAttachmentError] = useState<
    string | React.ReactElement | undefined
  >()
  const transactionId = useSelector(selectTransactionIdFromPathState)
  const { data: activeTransactionIntegration } =
    useQueryActiveTransactionIntegration(transactionId)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const user = useSelector(selectUserListModelFromState)
  const isPushingToERP = useSelector(selectIsPushingAttachmentsToERPFromState)

  const syncedWithIntegration = useMemo(
    () =>
      activeTransactionIntegration !== undefined &&
      activeTransactionIntegration.syncStatus ===
        ConnectedPlatformStatus.Success,
    [activeTransactionIntegration],
  )

  const syncAttachmentsError = useMemo(() => {
    return (
      attachments.find((a) => {
        const connection = a.connectedPlatforms?.find(
          (connection) =>
            connection.platform === activeIntegration?.connectedPlatform,
        )
        return (
          connection !== undefined &&
          connection.syncStatus === ConnectedPlatformStatus.Failed
        )
      }) !== undefined
    )
  }, [attachments])

  const attachmentsSyncedCount = useMemo(() => {
    return attachments.filter((a) => {
      const connection = a.connectedPlatforms?.find(
        (connection) =>
          connection.platform === activeIntegration?.connectedPlatform,
      )
      return (
        connection !== undefined &&
        connection.syncStatus === ConnectedPlatformStatus.Success
      )
    }).length
  }, [attachments])

  const dispatch = useFlyoutDispatch()

  const setErrors = (onlyOneReceipt: boolean): void => {
    if (errors) {
      setHasErrors(onlyOneReceipt)
    }
  }

  const handleTrackReceiptRulesEnabled = (enabled: boolean): void => {
    analytics?.track('Transactions - Modify Receipt rule - button press', {
      modification_type: enabled ? 'enabled' : 'disabled',
    })
  }
  const handleCheckboxClick = (): void => {
    setReceiptRequired(!receiptRequired)
    patchTransaction?.({
      receiptRulesEnabled: !receiptRequired,
    })
    handleTrackReceiptRulesEnabled(!receiptRequired)
  }

  const syncAttachments = (): void => {
    if (
      !isPushingToERP &&
      activeIntegration !== undefined &&
      activeTransactionIntegration !== undefined
    ) {
      dispatch(
        postERPReceiptAttachments(
          activeIntegration?.organizationId,
          activeIntegration?.id,
          activeTransactionIntegration?.transactionId,
        ),
      ).then((res) => {
        // TODO: move into onSuccess when receipt attachment actions are moved to React Query
        if (res.type === POST_ERP_RECEIPT_ATTACHMENTS_SUCCESS) {
          const raConnectedPlatforms = res.payload.receiptAttachments
          const lookup = new Set(
            raConnectedPlatforms.map((ra) => ra.receiptAttachmentId),
          )
          updateTransactionDetailResponse(queryClient, {
            transactionId,
            updater: (old) => ({
              ...old,
              receiptAttachments: old.receiptAttachments.map((ra) =>
                lookup.has(ra.id)
                  ? {
                      ...ra,
                      connectedPlatforms: raConnectedPlatforms.filter(
                        (cp) => cp.receiptAttachmentId === ra.id,
                      ),
                    }
                  : ra,
              ),
            }),
          })
        }
      })
      analytics?.track('sync transactions attachments to ERP', {
        count: attachments.length - attachmentsSyncedCount,
      })
    }
  }

  return (
    <Box
      css={css`
        display: flex;
        flex-direction: column;
        ${getBreakpointDown(breakpointSizes.XSM)(css`
          ${!!isTransaction &&
          css`
            margin-top: 10px;
          `}
        `)}
        ${!!isTransaction &&
        css`
          margin-top: 20px;
        `}
      `}
    >
      <div
        css={css`
          display: flex;
          margin-bottom: 40px;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
          ${attachments.length === 0 &&
          uploadAttachments === undefined &&
          css`
            margin-bottom: 30px;
          `}
        `}
      >
        <div
          css={css`
            display: flex;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-right: 10px;
              ${isTransaction &&
              css`
                ${getFontCss(
                  16,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            <Trns source={'Receipts'} />
          </span>
          <TestId testId={'receiptStatus'}>
            {attachments.length > 0 && (
              <Tag
                text={<Trns source={'Attached'} />}
                color={TagColorSchemes.Green}
              />
            )}
            {attachments.length === 0 && receiptRequired && (
              <Tag
                text={<Trns source={'Missing'} />}
                color={TagColorSchemes.Red}
              />
            )}
          </TestId>
        </div>
        {isTransaction && attachmentsSyncedCount > 0 && (
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
              display: flex;
              align-items: center;
            `}
          >
            <Trns
              source={'Synced to ${orgLvlIntegration}'}
              expressions={{
                orgLvlIntegration: activeIntegration?.platformName || '',
              }}
            />
            {activeIntegration?.iconElement ? (
              <span
                css={css`
                  width: 20px;
                  height: 20px;
                  margin-left: 4px;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;

                  svg {
                    width: 100%;
                    height: 100%;
                  }
                `}
                aria-hidden="true"
              >
                {activeIntegration.iconElement}
              </span>
            ) : (
              <img
                css={css`
                  width: 20px;
                  height: 20px;
                  margin-left: 4px;
                `}
                src={activeIntegration?.logo}
                alt={''}
              />
            )}
          </span>
        )}
      </div>
      <AttachmentsList
        attachments={attachments}
        uploadingAttachmentThumbnails={uploadingAttachmentThumbnails}
        uploadAttachments={uploadAttachments}
        deleteAttachment={deleteAttachment}
        isReceiptRequired={receiptRequired}
        setAttachmentError={setAttachmentError}
        errors={errors}
        setErrors={setErrors}
        showSyncStatus={user?.hasOrgBookkeeperPermissions}
        attachmentsText={
          receiptRequired ? (
            <>
              <SvgReceiptMissing
                css={css`
                  transform: scale(1.6);
                  margin-bottom: 10px;
                `}
              />
              <span
                css={css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <TestId testId={'addMissingReceipt'}>
                  <Trns source={'+ Add Missing Receipt'} />
                </TestId>
              </span>
            </>
          ) : (
            <>
              <SvgReceiptUpload
                css={css`
                  transform: scale(1.6);
                  margin-bottom: 10px;
                `}
              />
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.cta,
                    styleConstants.fontFamilies.medium,
                  )};
                `}
              >
                <Trns source={'+ Add Receipt'} />
              </span>
            </>
          )
        }
      />
      {(attachmentError || hasErrors || topLevelError) && (
        <ErrorList
          errors={[
            ...(attachmentError !== undefined ? [attachmentError] : []),
            ...(hasErrors
              ? [
                  <Trns
                    key={0}
                    source={'At least one receipt must be attached'}
                  />,
                ]
              : []),
            ...(topLevelError !== undefined ? [topLevelError] : []),
          ].filter((i) => i !== undefined)}
          css={css`
            padding-left: 0;
          `}
        />
      )}
      {isReceiptSectionVisible && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            padding: 20px 24px;
            margin-top: 30px;
            border: 1px solid ${styleConstants.colors.gray_light};
            background-color: ${styleConstants.colors.gray_lightest};
            border-radius: ${styleConstants.borderRadius.box};
          `}
        >
          <TestId testId={'enableReceiptRules'}>
            <Checkbox
              isSelected={receiptRequired}
              label={
                <span
                  css={css`
                    margin-top: 1px;
                  `}
                >
                  <Trns source={'Enable receipt rules for this transaction'} />
                </span>
              }
              onClick={(): void => {
                handleCheckboxClick()
              }}
            />
          </TestId>
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )};
              margin-top: 10px;
              margin-left: 32px;
            `}
          >
            <Trns
              source={
                'Receipts are required for transactions of at least ${amount}'
              }
              expressions={{
                amount:
                  receiptRule?.usdThresholdCents !== undefined &&
                  receiptRule?.cadThresholdCents !== undefined ? (
                    <Trns
                      source={'${usThreshold} and ${cadThreshold}'}
                      expressions={{
                        usThreshold: (
                          <Crncy
                            currency={ExtendCurrency.Usd}
                            amount={toDollars(receiptRule.usdThresholdCents)}
                          />
                        ),
                        cadThreshold: (
                          <Crncy
                            currency={ExtendCurrency.Cad}
                            amount={toDollars(receiptRule.cadThresholdCents)}
                          />
                        ),
                      }}
                    />
                  ) : (
                    <Crncy
                      currency={
                        receiptRule?.usdThresholdCents !== undefined
                          ? ExtendCurrency.Usd
                          : ExtendCurrency.Cad
                      }
                      amount={toDollars(
                        receiptRule?.usdThresholdCents !== undefined
                          ? receiptRule.usdThresholdCents
                          : (receiptRule?.cadThresholdCents ?? 0),
                      )}
                    />
                  ),
              }}
            />
          </span>
        </div>
      )}
      {isTransaction &&
        syncedWithIntegration &&
        activeIntegration &&
        activeIntegration.connectedPlatform !==
          OrganizationConnectedPlatform.QUICKBOOKS_DESKTOP &&
        user?.hasOrgBookkeeperPermissions && (
          <div
            onClick={syncAttachments}
            onKeyPress={syncAttachments}
            role={'button'}
            tabIndex={0}
            css={css`
              margin-top: 40px;
              display: flex;
              flex-direction: row;
              align-items: center;
              width: fit-content;
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
              ${(attachments.length <= 0 ||
                isPushingToERP ||
                attachmentsSyncedCount === attachments.length) &&
              css`
                opacity: 50%;
                pointer-events: none;
              `}

              &:hover {
                cursor: pointer;
              }
            `}
          >
            {isPushingToERP ? (
              <>
                <div
                  css={css`
                    display: flex;
                    transform: scale(calc(16 / 23));
                  `}
                >
                  <Processing />
                </div>
                <Trns
                  source={'Pushing to ${integration}...'}
                  expressions={{
                    integration: activeIntegration?.platformName,
                  }}
                />
              </>
            ) : (
              <>
                <Trns
                  source={'Push to ${integration}'}
                  expressions={{
                    integration: activeIntegration?.platformName,
                  }}
                />
                <SvgArrowForward
                  css={css`
                    transform: scale(calc(16 / 24));
                  `}
                />
              </>
            )}
          </div>
        )}
      {syncAttachmentsError && (
        <TopLevelError
          error={
            <span
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
                margin-top: 20px;
              `}
            >
              <Trns
                source={
                  'One or more receipts failed to push. This could be due to the attachment being in an unsupported image format or network errors. Please change the image format or try again later.'
                }
              />
            </span>
          }
        />
      )}
    </Box>
  )
}

export default ReceiptsBox
